import { useMemo } from 'react';
import { useAppState } from '../state/AppState';
import { 
  getDistinctRegions, 
  getSpecialtiesByRegion, 
  getInsurancesByRegion, 
  getAgeGroupsByRegion 
} from '../services/availabilityDataService';

/**
 * Custom hook for transforming availability data into UI-ready formats.
 * 
 * This hook provides pure functions that transform availability data
 * into the formats needed by different screens, following React best practices.
 */
export function useDataTransformation() {
  const { state } = useAppState();
  const availabilityData = state.availabilities;

  // Get distinct regions for state selection
  const regions = useMemo(() => {
    if (!availabilityData || availabilityData.length === 0) {
      return [];
    }
    return getDistinctRegions(availabilityData);
  }, [availabilityData]);

  // Get specialties for reason selection (filtered by region)
  const specialties = useMemo(() => {
    if (!availabilityData || availabilityData.length === 0) {
      return [];
    }
    const region = state.stateString;
    return getSpecialtiesByRegion(availabilityData, region || '');
  }, [availabilityData, state.stateString]);

  // Get insurances for insurance selection (filtered by region)
  const insurances = useMemo(() => {
    if (!availabilityData || availabilityData.length === 0) {
      return [];
    }
    const region = state.stateString;
    return getInsurancesByRegion(availabilityData, region || '');
  }, [availabilityData, state.stateString]);

  // Get age groups for age selection (filtered by region)
  const ageGroups = useMemo(() => {
    if (!availabilityData || availabilityData.length === 0) {
      return [];
    }
    const region = state.stateString;
    return getAgeGroupsByRegion(availabilityData, region || '');
  }, [availabilityData, state.stateString]);

  return {
    regions,
    specialties,
    insurances,
    ageGroups,
    hasAvailabilityData: availabilityData !== null && availabilityData.length > 0
  };
}

/**
 * Custom hook for getting appointment times from availability data.
 * 
 * Transforms availability data into appointment time slots for calendar display.
 */
export function useAppointmentTimes() {
  const { state } = useAppState();
  const availabilityData = state.availabilities;

  const appointmentTimes = useMemo(() => {
    if (!availabilityData || availabilityData.length === 0) {
      return [];
    }

    const selectedRegion = state.stateString;
    const selectedLocation = state.location;

    // Filter availabilities by region and location
    const filteredAvailabilities = availabilityData.filter(availability => {
      const regionMatch = !selectedRegion || 
        availability.region === selectedRegion;
      
      const locationMatch = !selectedLocation || 
        availability.locationName === selectedLocation ||
        (availability.locationType === 'Telehealth' && selectedLocation === 'Telehealth');
      
      return regionMatch && locationMatch;
    });

    // Transform to appointment times
    const times = filteredAvailabilities
      .filter(availability => availability.startDateIoi && availability.startTime)
      .map(availability => {
        const formattedDate = new Date(availability.startDateIoi).toISOString().split('T')[0];
        const timeRange = createTimeRange(availability.startTime, availability.locationName);
        
        return {
          id: `${availability.contactId}-${availability.providerCode}-${formattedDate}-${availability.startTime}`,
          date: formattedDate,
          time: timeRange
        };
      });

    return times;
  }, [availabilityData, state.stateString, state.location]);

  const availableDates = useMemo(() => {
    return Array.from(new Set(appointmentTimes.map(a => a.date))).sort();
  }, [appointmentTimes]);

  return {
    appointmentTimes,
    availableDates
  };
}

/**
 * Creates a time range string in the format "12:30pm - 1:30pm @ Location"
 * or "12pm - 1pm @ Location" for exact hours
 */
function createTimeRange(startTime: string, location: string): string {
  try {
    // Parse the start time (format: "09:30:00" or "14:30:00")
    const [hours, minutes] = startTime.split(':').map(Number);
    
    // Convert to 12-hour format
    const isPM = hours >= 12;
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    const displayMinutes = minutes === 0 ? '' : `:${minutes.toString().padStart(2, '0')}`;
    const ampm = isPM ? 'pm' : 'am';
    
    // Calculate end time (1 hour later)
    const endHours = hours + 1;
    const endIsPM = endHours >= 12;
    const endDisplayHours = endHours === 12 ? 12 : endHours > 12 ? endHours - 12 : endHours;
    const endDisplayMinutes = minutes === 0 ? '' : `:${minutes.toString().padStart(2, '0')}`;
    const endAmpm = endIsPM ? 'pm' : 'am';
    
    return `${displayHours}${displayMinutes}${ampm} - ${endDisplayHours}${endDisplayMinutes}${endAmpm}`;
  } catch (error) {
    return '10:30am';
  }
}
